import CryptoJS from 'crypto-js'
import moment from 'moment'

import api from '@/api'
import {
  KEY_BEK,
  KEY_IMM,
  KEY_JBG, KEY_KTD,
  KEY_TCM,
  KEY_VESSEL_LINE_UP_END_USER,
  KEY_VESSEL_LINE_UP_ID
} from '@/fixtures/Constants'
import { CRYPTO_KEY } from '@/fixtures/Hash'

const encryptedDetailId = btoa(KEY_VESSEL_LINE_UP_ID)
const encryptedDetailEndUser = btoa(KEY_VESSEL_LINE_UP_END_USER)

const namespaced = true

const state = {
  vesselLineUpId: localStorage.getItem(encryptedDetailId) || null,
  vesselLineUpEndUser: localStorage.getItem(encryptedDetailEndUser) || null,
  vesselLineUpList: [],
  vesselLineUpDetail: {},
  vesselLineUpLog: [],
  endUserList: [],
  endUserDetail: {},
  agencies: [],
  loadingFacilities: [],
  flfFcNominates: [],
  vesselTypes: [],
  vessel: [],
  barges: [],
  blendingSchemes: [],
  loadingPlanPreview: {},
  loadingPlanDetail: [],
  vesselCalendars: [],
  exportFile: {},
  exportTBSFile: {},
  polOptions: [],
  statusOptions: [],
  loadingPlanVLU: [],
  lastSyncTime: null
}

const getters = {
  vesselLineUpId: state => state.vesselLineUpId
    ? Number(CryptoJS.AES.decrypt(state.vesselLineUpId, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  vesselLineUpEndUser: state => state.vesselLineUpEndUser
    ? CryptoJS.AES.decrypt(state.vesselLineUpEndUser, CRYPTO_KEY).toString(CryptoJS.enc.Utf8)
    : null,
  vesselLineUpList: state => state.vesselLineUpList,
  vesselLineUpDetail: state => state.vesselLineUpDetail,
  vesselLineUpLog: state => state.vesselLineUpLog,
  endUserList: state => state.endUserList,
  endUserDetail: state => state.endUserDetail,
  agencies: state => state.agencies,
  loadingFacilities: state => state.loadingFacilities,
  flfFcNominates: state => state.flfFcNominates,
  vesselTypes: state => state.vesselTypes,
  vessel: state => state.vessel,
  blendingSchemes: state => state.blendingSchemes,
  barges: state => state.barges,
  loadingPlanPreview: state => state.loadingPlanPreview,
  loadingPlanDetail: state => state.loadingPlanDetail,
  vesselCalendars: state => state.vesselCalendars,
  exportFile: state => state.exportFile,
  exportTBSFile: state => state.exportTBSFile,
  polOptions: state => state.polOptions,
  statusOptions: state => state.statusOptions,
  loadingPlanVLU: state => state.loadingPlanVLU,
  lastSyncTime: state => state.lastSyncTime
}

const mutations = {
  SET_VESSEL_LINE_UP_ID: (state, payload) => {
    state.vesselLineUpId = payload
    localStorage.setItem(encryptedDetailId, payload)
  },
  SET_VESSEL_LINE_UP_END_USER: (state, payload) => {
    state.vesselLineUpEndUser = payload
    localStorage.setItem(encryptedDetailEndUser, payload)
  },
  SET_VESSEL_LINE_UP_LIST: (state, payload) => {
    state.vesselLineUpList = payload
  },
  SET_VESSEL_LINE_UP_DETAIL: (state, payload) => {
    state.vesselLineUpDetail = payload
  },
  SET_VESSEL_LINE_UP_LOG: (state, payload) => {
    state.vesselLineUpLog = payload
  },
  SET_END_USER_LIST: (state, payload) => {
    state.endUserList = payload
  },
  SET_END_USER_DETAIL: (state, payload) => {
    state.endUserDetail = payload
  },
  SET_AGENCIES: (state, payload) => {
    state.agencies = payload
  },
  SET_LOADING_FACILITIES: (state, payload) => {
    state.loadingFacilities = payload
  },
  SET_FLF_FC_NOMINATES: (state, payload) => {
    state.flfFcNominates = payload
  },
  SET_VESSEL_TYPES: (state, payload) => {
    state.vesselTypes = payload
  },
  SET_VESSEL: (state, payload) => {
    state.vessel = payload
  },
  SET_BARGES: (state, payload) => {
    state.barges = payload
  },
  SET_BLENDING_SCHEMES: (state, payload) => {
    state.blendingSchemes = payload
  },
  SET_LOADING_PLAN_PREVIEW: (state, payload) => {
    state.loadingPlanPreview = payload
  },
  SET_LOADING_PLAN_DETAIL: (state, payload) => {
    state.loadingPlanDetail = payload
  },
  SET_VESSEL_CALENDARS: (state, payload) => {
    state.vesselCalendars = payload
  },
  SET_EXPORT_FILE: (state, payload) => {
    state.exportFile = payload;
  },
  SET_EXPORT_TBS_FILE: (state, payload) => {
    state.exportTBSFile = payload;
  },
  SET_POL_OPTIONS: (state, payload) => {
    state.polOptions = payload;
  },
  SET_STATUS_OPTIONS: (state, payload) => {
    state.statusOptions = payload;
  },
  SET_LOADING_PLAN_VLU: (state, payload) => {
    state.loadingPlanVLU = payload;
  },
  SET_LAST_SYNC_TIME: (state, payload) => {
    state.lastSyncTime = payload;
  }
}

const actions = {
  async submitVesselLineUp({_}, payload) {
    try {
      if (payload.isCreate) {
        await api.postVesselLineUp(payload.form)
      } else {
        await api.putVesselLineUp(payload.id, payload.form)
      }
    } catch (err) {
      throw err
    }
  },
  async getVesselLineUpList({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage || 1,
        limit: filterConfig.pageSize || 5
      }
      const { data } = await api.getVesselLineUpList({...filter, ...payload})

      if (data.error) {
        commit('SET_VESSEL_LINE_UP_LIST', [])
        commit('Table/SET_TOTAL_DATA', 0, { root: true })
        commit('Table/SET_TOTAL_PAGE', 1, { root: true })
        return
      }

      commit('SET_VESSEL_LINE_UP_LIST', data.data)
      commit('SET_LAST_SYNC_TIME', data.last_sync_triggered_at)
      commit('Table/SET_TOTAL_DATA', data.total, { root: true })
      commit('Table/SET_TOTAL_PAGE', data.pages, { root: true })
    } catch (err) {
      throw err
    }
  },
  async getVesselLineUpDetail({ commit, dispatch }, payload) {
    try {
      const { data } = await api.getVesselLineUpDetail(payload)
      dispatch('setVesselLineUpId', payload)
      if (!data.error) {
        commit('SET_VESSEL_LINE_UP_DETAIL', data)
      }
    } catch (err) {
      throw err
    }
  },
  async getVesselLineUpLog({ commit }, payload) {
    try {
      const { data } = await api.getVesselLineUpLog(payload)
      if (!data.error) {
        commit('SET_VESSEL_LINE_UP_LOG', data)
      }
    } catch (err) {
      throw err
    }
  },
  async getVesselLineUpHistories({}, payload) {
    try {
      const { data } = await api.getVesselLineUpHistories(payload)
      return data
    } catch (err) {
      throw err
    }
  },
  async getEndUserList({ commit }, payload) {
    try {
      const { data } = await api.getEndUser(null, payload)
      if (!data.error) {
        const endUserList = data.map(item => ({
          text: item.end_user,
          value: item.end_user,
          id: item.id,
          sos_list_id: item.sos_list_id
        }))
        commit('SET_END_USER_LIST', endUserList)
        return
      }
      commit('SET_END_USER_LIST', [])
    } catch (err) {
      throw err
    }
  },
  async getEndUserDetail({ commit }, payload) {
    try {
      const { data } = await api.getEndUser(payload)

      if (!data.error) {
        const thirdParty = {}
        for (const [key, value] of Object.entries(data.third_party_coal)) {
          const comp = key.split('_')[0]
          thirdParty['third_party'] = {
            ...thirdParty['third_party'],
            [comp]: value
          }
        }

        const company = {}
        for (const [key, value] of Object.entries(data)) {
          switch (true) {
            case KEY_IMM.hasOwnProperty(key):
              company['imm'] = {
                ...company['imm'],
                [KEY_IMM[key]]: value
              }
              break
            case KEY_TCM.hasOwnProperty(key):
              company['tcm'] = {
                ...company['tcm'],
                [KEY_TCM[key]]: value
              }
              break
            case KEY_BEK.hasOwnProperty(key):
              company['bek'] = {
                ...company['bek'],
                [KEY_BEK[key]]: value
              }
              break
            case KEY_JBG.hasOwnProperty(key):
              company['jbg'] = {
                ...company['jbg'],
                [KEY_JBG[key]]: value
              }
              break
            case KEY_KTD.hasOwnProperty(key):
              company['ktd'] = {
                ...company['ktd'],
                [KEY_KTD[key]]: value
              }
              break
            default:
              break
          }
        }

        const detail = {
          name_of_vessel: data.name_of_vessel,
          demmurage: data.demurrage,
          company: data.company,
          lay: moment(data.lay).format('DD-MMM-YYYY'),
          can: moment(data.can).format('DD-MMM-YYYY'),
          l_port: data.l_port,
          destination: data.destination,
          ...company,
          ...thirdParty,
          total_tonnage: data.total_tonnage
        }

        commit('SET_END_USER_DETAIL', detail)
      }
    } catch (err) {
      throw err
    }
  },
  async getAgencies({ commit }) {
    try {
      const { data } = await api.getAgencyManagements( {
        is_master_data: false,
        status: true,
        sort_dir: 'asc',
        sort_by: 'name'
      })
      if (!data.error) {
        commit('SET_AGENCIES', data.map(item => (
          { text: item.name, value: item.id }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async getLoadingFacilities({ commit }) {
    try {
      const { data } = await api.getLoadingFacilities()
      if (!data.error) {
        commit('SET_LOADING_FACILITIES', data.map(item => (
          { text: item.name, value: item.id }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async getFlfFcNominates({ commit }) {
    try {
      const { data } = await api.getFlfFcNominates()
      if (!data.error) {
        commit('SET_FLF_FC_NOMINATES', data.map(item => (
          { text: item.name, value: item.id }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async getFlfFcNominatesByLoadingFacility({ commit }, payload) {
    try {
      const { data } = await api.getFlfFcNominatesByLoadingFacility(payload)
      if (!data.error) {
        commit('SET_FLF_FC_NOMINATES', data.map(item => (
          { text: item.name, value: item.id }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async getVesselTypes({ commit }) {
    try {
      const { data } = await api.getVesselTypes()
      if (!data.error) {
        commit('SET_VESSEL_TYPES', data.map(item => (
          { text: item.type, value: item.id }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async getVessel({ commit }) {
    try {
      const { data } = await api.getVessel()
      if (!data.error) {
        commit('SET_VESSEL', data.map(item => (
          { text: item, value: item }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async uploadLoadingPlan({ commit }, payload) {
    try {
      const formData = new FormData()
      const { vlu_id, ...formFields } = payload
      
      Object.entries(formFields).forEach(([key, value]) => {
        if (value) {
          formData.append(key, value)
        }
      })

      const { data } = await api.uploadLoadingPlan(formData, vlu_id, (progress) => {
        const totalLength = progress.lengthComputable
          ? progress.total
          : progress.target.getResponseHeader('content-length')
          || progress.target.getResponseHeader('x-decompressed-content-length')

        commit('UploadFile/SET_PROGRESS', totalLength
          ? Math.round((progress.loaded * 100) / totalLength)
          : 100,
        { root: true })
      })

      commit('SET_LOADING_PLAN_PREVIEW', data ? data : {})
    } catch (err) {
      throw err
    }
  },
  async submitLoadingPlan({ commit, dispatch }, payload) {
    try {
      await api.postLoadingPlan(payload)
      commit('SET_LOADING_PLAN_PREVIEW', {})
      dispatch('UploadFile/reset', { root: true })
    } catch (err) {
      throw err
    }
  },
  async getLoadingPlanDetail({ commit, dispatch }, payload) {
    try {
      const { data } = await api.getLatestLoadingPlan(payload.id)
      dispatch('setVesselLineUpId', payload.id)
      commit('SET_VESSEL_LINE_UP_END_USER', CryptoJS.AES.encrypt(payload.endUser.toString(), CRYPTO_KEY).toString())
      if (data.error) {
        commit('SET_LOADING_PLAN_DETAIL', [])
        return
      }
      commit('SET_LOADING_PLAN_DETAIL', data.loading_plan_details.length ? data.loading_plan_details : [])
    } catch (error) {
      throw error
    }
  },
  async getBarges({ commit }, payload) {
    try {
      const { data } = await api.getBargeLineUp(payload)
      if (data.error) {
        commit('SET_BARGES', [])
        return
      }

      commit('SET_BARGES', data)
    } catch (error) {
      throw error
    }
  },
  async getBlendingSchemes({ commit }) {
    try {
      const { data } = await api.getBlendingSchemes()
      if (!data.error) {
        commit('SET_BLENDING_SCHEMES', data.map(item => (
          { text: item.name, value: item.id }
        )))
      }
    } catch (err) {
      throw err
    }
  },
  async postBlendingSchemes({_}, payload) {
    try {
      await api.postBlendingSchemes(payload)
    } catch (err) {
      throw err
    }
  },
  async getVesselCalendar({ commit }, payload) {
    try {
      const { data } = await api.getVesselLineUpList(payload)

      if (data.error) {
        commit('SET_VESSEL_CALENDARS', [])
        return
      }

      commit('SET_VESSEL_CALENDARS', data)
    } catch (err) {
      throw err
    }
  },
  setVesselLineUpId({ commit }, payload) {
    commit('SET_VESSEL_LINE_UP_ID', CryptoJS.AES.encrypt(payload.toString(), CRYPTO_KEY).toString())
  },
  async downloadExport({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const response = await api.getVesselLineUpExcelDownload({
        search: filterConfig.search,
        ...payload})
      if (response) {
        commit('SET_EXPORT_FILE', response)
      }
    } catch (err) {
      throw err
    }
  },
  resetExportFiles({ commit }) {
    commit('SET_EXPORT_FILE', {})
  },
  async downloadExportTBS({ commit }, payload) {
    try {
      const response = await api.getVesselLineUpTBSExcelDownload(payload)
      if (response) {
        commit('SET_EXPORT_TBS_FILE', response)
      }
    } catch (err) {
      throw err
    }
  },
  resetExportTBSFiles({ commit }) {
    commit('SET_EXPORT_TBS_FILE', {})
  },
  async getVLUPolOptions({ commit }, payload) {
    try {
      const { data } = await api.getOptionsPol(payload)
      
      if (data?.error) {
        commit('SET_POL_OPTIONS', [])
        return
      }

      commit('SET_POL_OPTIONS', data.data.map(v => ({ text: v.name, value: v.name })))
    } catch (err) {
      throw err
    }
  },
  async getVLUStatusOptions({ commit }, payload) {
    try {
      const { data } = await api.getOptionsStatus(payload)
      
      if (data?.error) {
        commit('SET_STATUS_OPTIONS', [])
        return
      }
      
      commit('SET_STATUS_OPTIONS', data.data.map(v => ({ text: v.name, value: v.name })))
    } catch (err) {
      throw err
    }
  },
  async getLoadingPlanVLU({ commit }, payload) {
    try {
      const { data } = await api.getVLULoadingPlan(payload.id)
      if (data?.error) {
        commit('SET_LOADING_PLAN_VLU', [])
        return
      }
      commit('SET_LOADING_PLAN_VLU', data)
    } catch (err) {
      throw err
    }
  },
  async postVLUSync({ commit, __ }, _) {
    try {
      const { data } = await api.postVesselLineUpSync()
      if (data?.data) {
        commit('SET_LAST_SYNC_TIME', data.data.last_sync_triggered_at)
      }
    } catch (err) {
      throw err
    }
  },
  async postVLUSendToFLF({ commit, __ }, _) {
    try {
      const { data } = await api.postVesselLineUpSendToFLF()
    } catch (err) {
      throw err
    }
  }
}

export default { namespaced, state, getters, mutations, actions }
